import { useCallback, useRef } from "react";

const DRAG_THRESHOLD_PX = 5;

export default function useDragScroll<T extends HTMLElement>() {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((el: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!el) return;

    const state = {
      isDown: false,
      isDragging: false,
      startX: 0,
      startScrollLeft: 0,
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      // Don't hijack interactive controls (text inputs, selects, etc.)
      if (target.closest("input, textarea, select, [contenteditable='true']"))
        return;
      state.isDown = true;
      state.isDragging = false;
      state.startX = e.clientX;
      state.startScrollLeft = el.scrollLeft;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!state.isDown) return;
      const dx = e.clientX - state.startX;
      if (!state.isDragging && Math.abs(dx) > DRAG_THRESHOLD_PX) {
        state.isDragging = true;
        el.style.cursor = "grabbing";
        el.style.userSelect = "none";
      }
      if (state.isDragging) {
        e.preventDefault();
        el.scrollLeft = state.startScrollLeft - dx;
      }
    };

    const onMouseUp = () => {
      if (!state.isDown) return;
      state.isDown = false;
      el.style.cursor = "";
      el.style.userSelect = "";
      if (state.isDragging) {
        // The click event fires right after mouseup; keep isDragging set
        // until it has been suppressed, then reset asynchronously.
        setTimeout(() => {
          state.isDragging = false;
        }, 0);
      }
    };

    const onClickCapture = (e: MouseEvent) => {
      if (state.isDragging) {
        e.preventDefault();
        e.stopPropagation();
        state.isDragging = false;
      }
    };

    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    el.addEventListener("click", onClickCapture, true);

    cleanupRef.current = () => {
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("click", onClickCapture, true);
      el.style.cursor = "";
      el.style.userSelect = "";
    };
  }, []);
}
